import { useSettingsStore } from '@/store/settingsStore';
import { useQuery } from '@tanstack/react-query';
import { fetchProjects } from '@/features/projects/api/projects.api';
import type { Project } from '@/features/projects/api/projects.schemas';

/**
 * Hook to get projects from selected organization via API
 * Makes API call to /organizations/{orgId}/projects
 * 
 * Note: This hook only fetches projects. Store updates should be handled
 * by components that use this hook to avoid infinite re-render loops.
 */
export function useProjects() {
  const selectedOrganization = useSettingsStore((state) => state.selectedOrganization);

  // Fetch projects from API when organization is selected
  const {
    data: projectsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['projects', selectedOrganization?.id],
    queryFn: () => fetchProjects({ 
      page: 1, 
      per_page: 100,
      sort_by: 'name',
      sort_order: 'asc'
    }), // Get all projects
    enabled: !!selectedOrganization, // Only fetch when organization is selected
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Extract projects from API response
  const projects: Project[] = projectsResponse?.data || [];

  // Debug logging
  if (projects.length > 0) {
    console.log('useProjects: Projects loaded successfully', {
      selectedOrgId: selectedOrganization?.id,
      projectsCount: projects.length,
      projectNames: projects.map(p => p.name)
    });
  }

  return {
    projects,
    isLoading,
    error,
    refetch,
    hasNoProjects: projects.length === 0 && !isLoading && !!selectedOrganization,
  };
}